const localizedDigitMap = {
  "۰": "0",
  "۱": "1",
  "۲": "2",
  "۳": "3",
  "۴": "4",
  "۵": "5",
  "۶": "6",
  "۷": "7",
  "۸": "8",
  "۹": "9",
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
  "٫": ".",
  "٬": "",
};

const localizedDigitPattern = /[۰-۹٠-٩٫٬]/g;
const hasLocalizedDigitPattern = /[۰-۹٠-٩٫٬]/;

export const normalizeLocalizedNumbers = (value) =>
  String(value ?? "").replace(
    localizedDigitPattern,
    (digit) => localizedDigitMap[digit] ?? digit
  );

const shouldNormalizeElement = (element) => {
  if (!element || element.disabled || element.readOnly) return false;
  if (element.tagName === "TEXTAREA") return true;
  if (element.tagName !== "INPUT") return false;

  const type = (element.getAttribute("type") || "text").toLowerCase();
  return [
    "text",
    "number",
    "search",
    "tel",
    "url",
    "email",
    "password",
    "",
  ].includes(type);
};

const setNativeValue = (element, value) => {
  const descriptor = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  );
  const textareaDescriptor = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    "value"
  );
  const setter =
    descriptor?.set && element.tagName === "INPUT"
      ? descriptor.set
      : textareaDescriptor?.set;

  if (setter) {
    setter.call(element, value);
  } else {
    element.value = value;
  }
};

const syncNormalizedElementValue = (element) => {
  const currentValue = String(element.value ?? "");
  if (!hasLocalizedDigitPattern.test(currentValue)) return false;

  const normalizedValue = normalizeLocalizedNumbers(currentValue);
  if (normalizedValue === currentValue) return false;

  const start =
    typeof element.selectionStart === "number"
      ? element.selectionStart
      : currentValue.length;
  const end =
    typeof element.selectionEnd === "number"
      ? element.selectionEnd
      : start;
  const removedChars = currentValue.length - normalizedValue.length;

  setNativeValue(element, normalizedValue);

  try {
    const nextPosition = Math.max(start - removedChars, 0);
    const nextEnd = Math.max(end - removedChars, nextPosition);
    element.setSelectionRange(nextPosition, nextEnd);
  } catch (error) {
    // Some input types, including number, do not allow selection APIs.
  }

  return true;
};

const replaceElementValue = (element, text) => {
  const normalizedText = normalizeLocalizedNumbers(text);
  const currentValue = element.value || "";
  const start =
    typeof element.selectionStart === "number"
      ? element.selectionStart
      : currentValue.length;
  const end =
    typeof element.selectionEnd === "number"
      ? element.selectionEnd
      : start;

  setNativeValue(
    element,
    currentValue.slice(0, start) + normalizedText + currentValue.slice(end)
  );

  const nextPosition = start + normalizedText.length;
  try {
    element.setSelectionRange(nextPosition, nextPosition);
  } catch (error) {
    // Number inputs do not support selection ranges in some browsers.
  }

  element.dispatchEvent(new Event("input", { bubbles: true }));
};

export const installLocalizedNumberNormalizer = () => {
  if (typeof document === "undefined") return;
  if (window.__localizedNumberNormalizerInstalled) return;
  window.__localizedNumberNormalizerInstalled = true;

  document.addEventListener(
    "beforeinput",
    (event) => {
      if (!shouldNormalizeElement(event.target)) return;
      if (!event.data || !hasLocalizedDigitPattern.test(event.data)) return;

      event.preventDefault();
      replaceElementValue(event.target, event.data);
    },
    true
  );

  document.addEventListener(
    "paste",
    (event) => {
      if (!shouldNormalizeElement(event.target)) return;

      const pastedText = event.clipboardData?.getData("text");
      if (!pastedText || !hasLocalizedDigitPattern.test(pastedText)) return;

      event.preventDefault();
      replaceElementValue(event.target, pastedText);
    },
    true
  );

  document.addEventListener(
    "input",
    (event) => {
      if (!shouldNormalizeElement(event.target)) return;
      syncNormalizedElementValue(event.target);
    },
    true
  );

  document.addEventListener(
    "focusout",
    (event) => {
      if (!shouldNormalizeElement(event.target)) return;
      if (syncNormalizedElementValue(event.target)) {
        event.target.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    true
  );
};
